/**
 * How a loop repeats (docs/refactor/66): every so many minutes, or at clock times — every day, weekdays, chosen days,
 * once a month — with the cron itself behind "Avançado". One picker, used by "Mandar trabalhar › Em loop", by the prompt
 * box's "Quando fazer › Repetir em horários…" and by "Editar" in the list. The server computes the next runs, so what the
 * person sees before saving is what the scheduler will do.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { browserTimeZone, cronOf, INTERVALS, weekdayName, type ScheduleDraft, type ScheduleMode } from '../lib/schedule'
import { useDebounced } from '../lib/useDebounced'
import { formatInterval } from '../lib/workInterval'

const MODES: ScheduleMode[] = ['interval', 'daily', 'weekdays', 'days', 'monthly', 'custom']
/** Monday first, as people read a week. */
const WEEK = [1, 2, 3, 4, 5, 6, 0]

export default function SchedulePicker({ value, onChange }: { value: ScheduleDraft; onChange: (next: ScheduleDraft) => void }) {
  const { t, locale } = useI18n()
  const zone = browserTimeZone()
  const set = (patch: Partial<ScheduleDraft>) => onChange({ ...value, ...patch })
  const cron = useDebounced(cronOf(value), 400)

  const preview = useQuery({
    queryKey: ['schedule-preview', cron, zone],
    queryFn: () => api.workOrders.previewSchedule(cron!, zone),
    enabled: !!cron && cron.split(' ').length === 5,
    staleTime: 60_000,
    retry: false,
  })

  const toggleDay = (day: number) =>
    set({ days: value.days.includes(day) ? value.days.filter((d) => d !== day) : [...value.days, day] })

  return (
    <div className="stack schedule-picker" style={{ gap: 8 }}>
      <div className="row row--wrap" style={{ gap: 8 }}>
        <label className="field" style={{ minWidth: 190, flex: 1 }}>
          <span className="field__label">{t('schedule.how')}</span>
          <select className="select" value={value.mode} onChange={(e) => set({ mode: e.target.value as ScheduleMode })}>
            {MODES.map((mode) => (
              <option key={mode} value={mode}>{t(`schedule.mode.${mode}`)}</option>
            ))}
          </select>
        </label>

        {value.mode === 'interval' && (
          <label className="field" style={{ width: 160 }}>
            <span className="field__label">{t('work.every')}</span>
            <select className="select" value={value.minutes} onChange={(e) => set({ minutes: Number(e.target.value) })}>
              {INTERVALS.map((minutes) => (
                <option key={minutes} value={minutes}>{formatInterval(t, minutes)}</option>
              ))}
            </select>
          </label>
        )}

        {value.mode === 'monthly' && (
          <label className="field" style={{ width: 110 }}>
            <span className="field__label">{t('schedule.dayOfMonth')}</span>
            <input className="input" type="number" min={1} max={28} value={value.dayOfMonth}
              onChange={(e) => set({ dayOfMonth: Math.min(Math.max(Number(e.target.value) || 1, 1), 28) })} />
          </label>
        )}

        {value.mode !== 'interval' && value.mode !== 'custom' && (
          <label className="field" style={{ width: 120 }}>
            <span className="field__label">{t('schedule.at')}</span>
            <input className="input" type="time" value={value.time} onChange={(e) => set({ time: e.target.value || '09:00' })} />
          </label>
        )}
      </div>

      {value.mode === 'days' && (
        <div className="row row--wrap" role="group" aria-label={t('schedule.mode.days')} style={{ gap: 4 }}>
          {WEEK.map((day) => (
            <button key={day} type="button" aria-pressed={value.days.includes(day)}
              className={`btn btn--sm ${value.days.includes(day) ? 'btn--primary' : 'btn--ghost'}`} onClick={() => toggleDay(day)}>
              {weekdayName(locale, day)}
            </button>
          ))}
        </div>
      )}

      {value.mode === 'custom' && (
        <label className="field">
          <span className="field__label">{t('schedule.cron')}</span>
          <input className="input mono" value={value.cron} spellCheck={false} placeholder="0 9 * * 1-5"
            onChange={(e) => set({ cron: e.target.value })} />
          <span className="tiny muted">{t('schedule.cronHint')}</span>
        </label>
      )}

      {cron && preview.data?.error && <span className="small" role="alert" style={{ color: 'var(--danger-text)' }}>{t(`work.error.${preview.data.error}`)}</span>}
      {cron && preview.data && !preview.data.error && preview.data.next.length > 0 && (
        <span className="tiny muted">
          {t('schedule.next', {
            when: preview.data.next.map((at) => new Date(at).toLocaleString(locale, { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })).join(' · '),
          })}
        </span>
      )}
      <span className="tiny muted">{value.mode === 'interval' ? t('schedule.whereRuns') : `${t('schedule.whereRuns')} ${t('schedule.zone', { zone })}`}</span>
    </div>
  )
}
